import update from "immutability-helper";
import React from "react";
import { Button, Checkbox, ControlLabel, FormControl, FormGroup, Modal, Radio, } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { triggerDialogLink } from "../actions";
import { closeDialog, closeDialogs } from "../actions/notifications";
import bbcode from "../controls/bbcode";
import Collapse from "../controls/Collapse";
import ErrorBoundary from "../controls/ErrorBoundary";
import Icon from "../controls/Icon";
import Webview from "../controls/Webview";
import { MutexWrapper } from "../util/MutexContext";
import { getPreloadApi, getWindowId } from "../util/preloadAccess";
const nop = () => undefined;
const Action = (props) => {
    const { action, isDefault, isDisabled, onDismiss } = props;
    const { t } = useTranslation(["common"]);
    const dismiss = React.useCallback(() => {
        onDismiss(action);
    }, [onDismiss, action]);
    return (React.createElement(Button, { autoFocus: isDefault, bsStyle: isDefault ? "primary" : undefined, disabled: isDisabled, id: "close", onClick: dismiss }, t(action)));
};
export const Dialog = () => {
    const { t } = useTranslation(["common"]);
    const dispatch = useDispatch();
    // Redux state
    const dialogs = useSelector((state) => state.session.notifications.dialogs);
    // Local state
    const [currentDialogId, setCurrentDialogId] = React.useState(undefined);
    const [dialogState, setDialogState] = React.useState(undefined);
    const [conditionResults, setConditionResults] = React.useState([]);
    // Refs for callbacks
    const stateRef = React.useRef({
        dialogs,
        currentDialogId,
        dialogState,
        conditionResults,
    });
    stateRef.current = {
        dialogs,
        currentDialogId,
        dialogState,
        conditionResults,
    };
    // Dispatch callbacks
    const onDismiss = React.useCallback((id, action, input) => {
        dispatch(closeDialog(id, action, input));
    }, [dispatch]);
    const onDismissMultiple = React.useCallback((ids, action, input) => {
        dispatch(closeDialogs(ids, action, input));
    }, [dispatch]);
    // Helper functions
    const validateContent = React.useCallback((content) => {
        if (content?.condition === undefined) {
            return undefined;
        }
        return content.condition(content);
    }, []);
    const translateParts = React.useCallback((message, tFunc, parameters) => {
        return (message || "")
            .split("\n")
            .map((line) => line
            .split("\t")
            .map((block) => tFunc(block, {
            replace: parameters,
            count: parameters && typeof parameters["count"] === "number"
                ? parameters["count"]
                : undefined,
        }))
            .join(" "))
            .join("\n");
    }, []);
    const iconForType = React.useCallback((type) => {
        switch (type) {
            case "info":
                return React.createElement(Icon, { className: "icon-info", name: "dialog-info" });
            case "error":
                return React.createElement(Icon, { className: "icon-error", name: "dialog-error" });
            case "question":
                return React.createElement(Icon, { className: "icon-question", name: "dialog-question" });
            default:
                return null;
        }
    }, []);
    const focusMe = React.useCallback((ele) => {
        if (ele !== null) {
            setTimeout(() => ele.focus(), 100);
        }
    }, []);
    const getValidationResult = React.useCallback((input) => {
        const { conditionResults: results } = stateRef.current;
        return results.filter((res) => res.id === input.id);
    }, []);
    // Event handlers
    const changeInput = React.useCallback((inputId, value) => {
        const { dialogState: state } = stateRef.current;
        if (!state)
            return;
        const idx = state.input.findIndex((form) => form.id === inputId);
        const newInput = { ...state.input[idx] };
        newInput.value = value;
        const newDialogState = update(state, {
            input: { $splice: [[idx, 1, newInput]] },
        });
        setDialogState(newDialogState);
        const validationResults = validateContent(newDialogState);
        if (validationResults !== undefined) {
            setConditionResults(validationResults);
        }
    }, [validateContent]);
    const toggleCheckbox = React.useCallback((checkboxId) => {
        const { dialogState: state } = stateRef.current;
        if (!state)
            return;
        const idx = state.checkboxes.findIndex((box) => box.id === checkboxId);
        if (idx === -1) {
            return;
        }
        const newCheckboxes = JSON.parse(JSON.stringify(state.checkboxes.slice(0)));
        newCheckboxes[idx].value = !newCheckboxes[idx].value;
        const newDialogState = update(state, {
            checkboxes: { $set: newCheckboxes },
        });
        setDialogState(newDialogState);
        const validationResults = validateContent(newDialogState);
        if (validationResults !== undefined) {
            setConditionResults(validationResults);
        }
    }, [validateContent]);
    const enableMultiple = React.useCallback((enabled = true) => {
        const { dialogState: state } = stateRef.current;
        if (!state)
            return;
        const newCheckboxes = JSON.parse(JSON.stringify(state.checkboxes.slice(0))).map((box) => ({ ...box, value: enabled }));
        const newDialogState = update(state, {
            checkboxes: { $set: newCheckboxes },
        });
        setDialogState(newDialogState);
        const validationResults = validateContent(newDialogState);
        if (validationResults !== undefined) {
            setConditionResults(validationResults);
        }
    }, [validateContent]);
    const enableAll = React.useCallback(() => {
        enableMultiple(true);
    }, [enableMultiple]);
    const disableAll = React.useCallback(() => {
        enableMultiple(false);
    }, [enableMultiple]);
    const toggleRadio = React.useCallback((radioId) => {
        const { dialogState: state } = stateRef.current;
        if (!state)
            return;
        const idx = state.choices.findIndex((box) => box.id === radioId);
        if (idx < 0) {
            return;
        }
        const newChoices = state.choices.map((choice) => ({
            ...choice,
            value: false,
        }));
        newChoices[idx].value = true;
        const newDialogState = update(state, {
            choices: { $set: newChoices },
        });
        setDialogState(newDialogState);
        const validationResults = validateContent(newDialogState);
        if (validationResults !== undefined) {
            setConditionResults(validationResults);
        }
    }, [validateContent]);
    const triggerLink = React.useCallback((linkIdx) => {
        const { dialogs: d } = stateRef.current;
        triggerDialogLink(d[0].id, linkIdx);
    }, []);
    const dismiss = React.useCallback((action) => {
        const { dialogs: d, dialogState: state } = stateRef.current;
        const data = {};
        if (state?.checkboxes !== undefined) {
            state.checkboxes.forEach((box) => {
                data[box.id] = box.value;
            });
        }
        if (state?.choices !== undefined) {
            state.choices.forEach((box) => {
                data[box.id] = box.value;
            });
        }
        if (state?.input !== undefined) {
            state.input.forEach((input) => {
                data[input.id] = input.value;
            });
        }
        setCurrentDialogId(undefined);
        setDialogState(undefined);
        // If "remember" is checked, apply to all pending dialogs with the same title
        const rememberChecked = data["remember"] === true;
        if (rememberChecked && d.length > 1) {
            const currentDialog = d[0];
            const dialogActions = currentDialog.actions;
            // Find all pending dialogs with the same title (indicating same type of dialog)
            const matchingDialogIds = d
                .filter((dialog) => dialog.title === currentDialog.title ||
                JSON.stringify(dialogActions) === JSON.stringify(dialog.actions))
                .map((dialog) => dialog.id);
            onDismissMultiple(matchingDialogIds, action, data);
        }
        else {
            onDismiss(d[0].id, action, data);
        }
    }, [onDismiss, onDismissMultiple]);
    const handleKeyPress = React.useCallback((evt) => {
        if (!evt.defaultPrevented) {
            const { dialogs: d, conditionResults: results } = stateRef.current;
            const dialog = d[0];
            if (evt.key === "Enter") {
                if (dialog.defaultAction !== undefined) {
                    evt.preventDefault();
                    const filterFunc = (res) => res.actions.find((act) => act === dialog.defaultAction) !==
                        undefined;
                    const isDisabled = results.find(filterFunc) !== undefined;
                    if (!isDisabled) {
                        dismiss(dialog.defaultAction);
                    }
                }
            }
        }
    }, [dismiss]);
    // Render helpers
    const renderInput = React.useCallback((input, idx) => {
        const { dialogState: state } = stateRef.current;
        let valRes;
        if (state?.condition !== undefined) {
            valRes = getValidationResult(input);
        }
        const validationState = valRes !== undefined
            ? valRes.length !== 0
                ? "error"
                : "success"
            : null;
        let effectiveType = input.type || "text";
        if (input.type === "multiline") {
            effectiveType = "text";
        }
        return (React.createElement(FormGroup, { key: input.id, validationState: validationState },
            input.label ? React.createElement(ControlLabel, null, t(input.label)) : null,
            React.createElement(FormControl, { componentClass: input.type === "multiline" ? "textarea" : undefined, id: `dialoginput-${input.id}`, inputRef: idx === 0 ? focusMe : undefined, label: input.label, placeholder: input.placeholder, type: effectiveType, value: input.value || "", onChange: (e) => {
                    if (e.target instanceof HTMLInputElement ||
                        e.target instanceof HTMLTextAreaElement) {
                        changeInput(input.id, e.target.value);
                    }
                } }),
            valRes !== undefined && valRes.length !== 0 ? (React.createElement("label", { className: "control-label" }, valRes.map((res) => res.errorText).join("\n"))) : null));
    }, [t, changeInput, focusMe, getValidationResult]);
    const renderButton = React.useCallback((link, idx) => {
        return (React.createElement("div", { key: idx },
            React.createElement(Button, { onClick: () => triggerLink(idx) }, link.label)));
    }, [triggerLink]);
    const renderLink = React.useCallback((link, idx) => {
        return (React.createElement("div", { key: idx },
            React.createElement("a", { onClick: (e) => {
                    e.preventDefault();
                    triggerLink(idx);
                } }, link.label)));
    }, [triggerLink]);
    const renderCheckbox = React.useCallback((checkbox, content) => {
        const translated = content.options?.translated !== false;
        const text = checkbox.bbcode !== undefined
            ? bbcode(translated
                ? t(checkbox.bbcode, {
                    replace: content.parameters,
                    count: content.parameters?.count,
                })
                : checkbox.bbcode, content.options?.bbcodeContext)
            : translated
                ? t(checkbox.text, {
                    replace: content.parameters,
                    count: content.parameters?.count,
                })
                : checkbox.text;
        return (React.createElement(Checkbox, { checked: checkbox.value, disabled: checkbox.disabled, id: checkbox.id, key: checkbox.id, onChange: () => toggleCheckbox(checkbox.id) }, text));
    }, [t, toggleCheckbox]);
    const renderRadiobutton = React.useCallback((checkbox) => {
        const content = (React.createElement("div", null, checkbox.subText !== undefined ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "choice-maintext" }, t(checkbox.text)),
            React.createElement("div", { className: "choice-subtext" }, t(checkbox.subText)))) : (React.createElement("div", { className: "choice-text" }, t(checkbox.text)))));
        return (React.createElement(Radio, { checked: checkbox.value, disabled: checkbox.disabled, id: checkbox.id, key: checkbox.id, name: "dialog-radio", onChange: () => toggleRadio(checkbox.id) }, content));
    }, [t, toggleRadio]);
    const renderContent = React.useCallback((content) => {
        let tFunc = t;
        if (content.options?.translated) {
            // bit of a hack, setting lngs to empty list so that no translation happens,
            // but we still make use of the i18next interpolator
            tFunc = ((input, options) => t(input, { ...options, lngs: [] }));
        }
        const controls = [];
        if (content.text) {
            controls.push({
                id: "text",
                control: (React.createElement("div", { className: "dialog-content-text", key: "dialog-content-text" }, tFunc(content.text, {
                    replace: content.parameters,
                    count: content.parameters?.count,
                }))),
            });
        }
        if (content.bbcode !== undefined) {
            // BBCode content is always raw text, not translation keys
            // Bypass i18next entirely to avoid key separator parsing
            let bbcodeContent = content.bbcode;
            // Manual parameter interpolation
            if (content.parameters) {
                Object.keys(content.parameters).forEach((key) => {
                    const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
                    bbcodeContent = bbcodeContent.replace(regex, String(content.parameters[key]));
                });
            }
            controls.push({
                id: "bbcode",
                control: (React.createElement("div", { className: "dialog-content-bbcode", key: "dialog-content-bbcode" }, bbcode(bbcodeContent, content.options?.bbcodeContext))),
            });
        }
        if (content.md !== undefined) {
            controls.push({
                id: "md",
                control: (React.createElement("div", { className: "dialog-content-markdown", key: "dialog-content-markdown" },
                    React.createElement(ReactMarkdown, null, tFunc(content.md, {
                        replace: content.parameters,
                        count: content.parameters?.count,
                    })))),
            });
        }
        if (content.message !== undefined) {
            const wrap = content.options !== undefined && content.options.wrap === true
                ? "on"
                : "off";
            const ctrl = (React.createElement("textarea", { key: "dialog-content-message", readOnly: true, value: translateParts(content.message, tFunc, content.parameters), wrap: wrap }));
            if (content.options !== undefined &&
                content.options.hideMessage === true) {
                controls.push({
                    id: "message",
                    control: (React.createElement(Collapse, { hideText: t("Hide Details"), key: "dialog-content-message-wrapper", showText: t("Show Details") }, ctrl)),
                });
            }
            else {
                controls.push({ id: "message", control: ctrl });
            }
        }
        if (content.htmlFile !== undefined) {
            controls.push({
                id: "htmlFile",
                control: (React.createElement("div", { key: "dialog-content-html-file" },
                    React.createElement(Webview, { src: `file://${content.htmlFile}` }))),
            });
        }
        if (content.htmlText !== undefined) {
            controls.push({
                id: "htmlText",
                control: (React.createElement("div", { className: "dialog-content-html", 
                    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
                    dangerouslySetInnerHTML: { __html: content.htmlText }, key: "dialog-content-html-text" })),
            });
        }
        if (content.input !== undefined) {
            controls.push({
                id: "input",
                control: (React.createElement("div", { className: "dialog-content-input", key: "dialog-form-content" }, content.input.map(renderInput))),
            });
        }
        if (content.checkboxes !== undefined) {
            controls.push({
                id: "checkboxes",
                control: (React.createElement("div", { className: "dialog-content-choices", key: "dialog-content-checkboxes" },
                    content.checkboxes.length > 3 ? (React.createElement("div", { className: "dialog-apply-all-btns" },
                        React.createElement("a", { onClick: enableAll }, t("Enable all")),
                        "\u00A0",
                        React.createElement("a", { onClick: disableAll }, t("Disable all")))) : null,
                    React.createElement("div", null, content.checkboxes.map((checkbox) => renderCheckbox(checkbox, content))))),
            });
        }
        if (content.choices !== undefined) {
            controls.push({
                id: "choices",
                control: (React.createElement("div", { className: "dialog-content-choices", key: "dialog-content-choices" },
                    React.createElement("div", null, content.choices.map(renderRadiobutton)))),
            });
        }
        if (content.links !== undefined) {
            controls.push({
                id: "links",
                control: (React.createElement("div", { key: "dialog-form-links" }, content.links.map(content.options?.linksAsButtons ? renderButton : renderLink))),
            });
        }
        if (content.options?.order !== undefined) {
            const { order } = content.options;
            controls.sort((lhs, rhs) => {
                const lIdx = order.includes(lhs.id)
                    ? 100 + order.indexOf(lhs.id)
                    : controls.indexOf(lhs);
                const rIdx = order.includes(rhs.id)
                    ? 100 + order.indexOf(rhs.id)
                    : controls.indexOf(rhs);
                return lIdx - rIdx;
            });
        }
        return (React.createElement("div", { className: "dialog-container" }, controls.map((iter) => iter.control)));
    }, [
        t,
        translateParts,
        renderInput,
        renderCheckbox,
        renderRadiobutton,
        renderButton,
        renderLink,
        enableAll,
        disableAll,
    ]);
    const renderAction = React.useCallback((action, isDefault) => {
        const { conditionResults: results } = stateRef.current;
        const isDisabled = results.find((res) => res.actions.find((act) => act === action) !== undefined) !== undefined;
        return (React.createElement(Action, { action: action, isDefault: isDefault, isDisabled: isDisabled, key: action, onDismiss: dismiss }));
    }, [dismiss]);
    // Previous dialogs ref for comparison
    const prevDialogsRef = React.useRef(dialogs);
    // Handle dialogs changes (replaces componentWillReceiveProps and componentDidMount)
    React.useEffect(() => {
        if (dialogs.length > 0) {
            const prevDialogs = prevDialogsRef.current;
            const prevCurrentId = stateRef.current.currentDialogId;
            if (dialogs[0].id !== prevCurrentId) {
                // dialog changed
                const newDialogState = dialogs[0].content;
                setCurrentDialogId(dialogs[0].id);
                setDialogState(newDialogState);
                const validationResults = validateContent(newDialogState);
                if (validationResults !== undefined) {
                    setConditionResults(validationResults);
                }
                // Bring window to focus when a new dialog appears
                const winId = getWindowId();
                const api = getPreloadApi();
                void api.window.isMinimized(winId).then((minimized) => {
                    if (minimized) {
                        void api.window.restore(winId);
                    }
                    void api.window.setAlwaysOnTop(winId, true);
                    void api.window.show(winId);
                    void api.window.setAlwaysOnTop(winId, false);
                });
            }
            else if (prevDialogs[0]?.content !== dialogs[0]?.content) {
                // same dialog id but maybe the content changed?
                const newDialogState = dialogs[0].content;
                setDialogState(newDialogState);
                const validationResults = validateContent(newDialogState);
                if (validationResults !== undefined) {
                    setConditionResults(validationResults);
                }
            }
        }
        prevDialogsRef.current = dialogs;
    }, [dialogs, validateContent]);
    // Render
    const dialog = dialogs.length > 0 ? dialogs[0] : undefined;
    if (dialog === undefined || dialogState === undefined) {
        return null;
    }
    const type = dialog.content.htmlFile !== undefined ||
        dialog.content.htmlText !== undefined
        ? "wide"
        : "regular";
    return (React.createElement(MutexWrapper, { show: dialog !== undefined },
        React.createElement(Modal, { className: `
          common-dialog-${type}
        `, id: dialog.id, show: dialog !== undefined, onHide: nop, onKeyPress: handleKeyPress },
            React.createElement(Modal.Header, null,
                React.createElement(Modal.Title, null,
                    iconForType(dialog.type),
                    " ",
                    t(dialog.title, {
                        replace: dialog.content.parameters,
                        count: dialog.content.parameters?.count,
                    }))),
            React.createElement(Modal.Body, null,
                React.createElement(ErrorBoundary, { visible: true }, renderContent(dialogState))),
            React.createElement(Modal.Footer, null, dialog.actions.map((action) => renderAction(action, action === dialog.defaultAction))))));
};
export default Dialog;
